// Coach API — consumer-portal endpoints for the Coach surface, History
// (sessions rail) and quality-loop feedback.
//
// Routes (all consumer-pool only, served under /me/coach):
//
//   Sessions
//     GET    /me/coach/sessions                    — list user's sessions
//     POST   /me/coach/sessions                    — create a new session
//     GET    /me/coach/sessions/{id}               — session metadata
//     PATCH  /me/coach/sessions/{id}               — rename, pin, archive, end
//     DELETE /me/coach/sessions/{id}               — delete session (+ messages,
//                                                     optionally derived notes)
//     GET    /me/coach/sessions/{id}/messages      — list a session's messages
//
//   Messages
//     POST   /me/coach/messages                    — send a user message in a
//                                                     session; returns reply
//
//   Feedback
//     POST   /me/coach/feedback                    — thumbs ± + optional reason

// ─── Shared value types ───────────────────────────────────────────────────

export type CoachMessageRole = 'user' | 'coach';

export interface CoachCitation {
  title: string;
  /** RAG relevance score 0–1. Kept on the wire for debug / Labs; the
   *  consumer UI deliberately does not show it (per designer Q3). */
  score: number;
  href?: string;
  /** KB entry type, when the citation resolves to a specific entry. Drives
   *  the type badge (Guidance / Framework / Case study / Book excerpt) in
   *  the SourcesLine + ConsultedPanel. */
  type?: 'qa_pair' | 'framework' | 'case_study' | 'document_excerpt';
  /** Short excerpt from the entry (first ~2-4 sentences), shown when the
   *  user expands a row in the ConsultedPanel. Omitted when the engine
   *  wasn't able to attach one (e.g. legacy non-RAG replies). */
  excerpt?: string;
  /** Domain code (D1..D8), when the citation resolves to a specific entry.
   *  Shown as a mono eyebrow on each panel row. */
  domain?: string;
}

export interface CoachMessage {
  id: string;
  sessionId: string;
  role: CoachMessageRole;
  text: string;
  citations?: CoachCitation[];
  /** ISO-8601 server-side timestamp. */
  createdAt: string;
  /** Engine that produced this message. `'placeholder'` = LLM provider not yet wired. */
  provider?: 'bedrock' | 'placeholder';
}

export interface CoachSession {
  id: string;
  /** Auto-generated by Haiku at session end; `null` while pending (shown as "New session · {date}"). */
  title: string | null;
  /** `true` when the user renamed; the auto-titler never overwrites a locked title. */
  titleLocked?: boolean;
  /** Coach-authored one-liner for the sidebar; `null` until the auto-titler runs. */
  summary: string | null;
  /** Set when the session was opened from a framework workspace. */
  frameworkInstanceId?: string;
  /** Set when the user has assigned this session to a project. One project
   *  per session in V1 (per designer response Q7 + §A.2). */
  projectId?: string;
  pinned?: boolean;
  archived?: boolean;
  messageCount: number;
  /** ISO-8601 timestamps. */
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string;
  /** When the session was ended (explicit "End session" or 30-min inactivity timeout). */
  endedAt?: string;
}

// ─── GET /me/coach/sessions ────────────────────────────────────────────────

export interface ListCoachSessionsResponse {
  sessions: CoachSession[];
}

// ─── POST /me/coach/sessions ───────────────────────────────────────────────

export interface CreateCoachSessionRequest {
  /** Attach the new session to a framework instance (set when opened from a
   *  framework workspace). The sessions rail then surfaces a framework chip. */
  frameworkInstanceId?: string;
  /** Attach the new session to a project. Typically set when a project filter
   *  is active in the rail (per designer Q10 — "if a project filter is
   *  active, new sessions go into that project"). */
  projectId?: string;
}

export interface CreateCoachSessionResponse {
  session: CoachSession;
}

// ─── GET /me/coach/sessions/{id} ───────────────────────────────────────────

export interface GetCoachSessionResponse {
  session: CoachSession;
}

// ─── PATCH /me/coach/sessions/{id} ─────────────────────────────────────────

export interface PatchCoachSessionRequest {
  /** User rename. Sets titleLocked=true so the auto-titler never overwrites. */
  title?: string;
  pinned?: boolean;
  archived?: boolean;
  /** Mark the session ended now (explicit "End session" from the UI). */
  end?: boolean;
  /** Move the session to a project (or `null` to unassign — "Remove from
   *  project" in the SessionItem menu). */
  projectId?: string | null;
}

export interface PatchCoachSessionResponse {
  session: CoachSession;
}

// ─── DELETE /me/coach/sessions/{id} ────────────────────────────────────────

export interface DeleteCoachSessionRequest {
  /** When true, also delete memory notes inferred from this session.
   *  Defaults to false; the UI confirm dialog toggles it per the designer's Q20. */
  alsoDeleteInferredNotes?: boolean;
}

export interface DeleteCoachSessionResponse {
  deleted: {
    sessionId: string;
    messagesDeleted: number;
    inferredNotesDeleted: number;
  };
}

// ─── GET /me/coach/sessions/{id}/messages ──────────────────────────────────

export interface ListSessionMessagesResponse {
  messages: CoachMessage[];
}

// ─── POST /me/coach/messages ───────────────────────────────────────────────

/**
 * Build a rail-friendly session title from a user's first message. Pure
 * function, shared between the backend (sets this at session create) and
 * the SPA (fabricates the local session row before the next list reload
 * returns the server-persisted title). Truncates to `maxChars`, prefers a
 * word boundary, trims trailing punctuation, appends an ellipsis when
 * trimmed. Returns '' for empty / whitespace-only input.
 */
export function titleFromMessage(text: string, maxChars = 60): string {
  const normalized = text.trim().replace(/\s+/g, ' ');
  if (normalized.length === 0) return '';
  if (normalized.length <= maxChars) return normalized;
  const window = normalized.slice(0, maxChars);
  const lastSpace = window.lastIndexOf(' ');
  const base = lastSpace > maxChars * 0.5 ? window.slice(0, lastSpace) : window;
  return base.replace(/[,.;:!?\s\-–—]+$/, '') + '…';
}

export interface SendCoachMessageRequest {
  /** Raw text from the composer. Empty / whitespace-only → 400. */
  text: string;
  /**
   * Session to append the message to. When omitted the server creates a
   * fresh session, Claude.ai-style, with its title derived from this
   * message. Omit it to start a new chat; pass it to continue an existing
   * one.
   */
  sessionId?: string;
  /**
   * Only read when `sessionId` is omitted. Files the freshly-created
   * session into this project (the "+ New session" button inside a project
   * filter sends it so the new session lands in the right place).
   */
  projectId?: string;
  /**
   * Optional scope hint surfaced in-chat via the "Talk about" picker.
   * V1 supports 'general' only; V1.x adds 'framework:<id>' + 'activity:<id>'.
   */
  scope?: 'general';
}

export interface SendCoachMessageResponse {
  /** The newly-persisted USER message. */
  userMessage: CoachMessage;
  /** The coach's reply (placeholder text in V1; real Bedrock when unblocked). */
  coachMessage: CoachMessage;
  /** The session the pair landed on (useful when the server resolved it). */
  sessionId: string;
}

// ─── POST /me/coach/messages/stream (SSE) ──────────────────────────────────
//
// Streaming counterpart to POST /me/coach/messages. Same request shape,
// response is `text/event-stream`. MR A.5 emits pipeline-stage events only
// (retrieving → drafting → done); MR F adds `token` events in the drafting
// phase for word-by-word rendering.
//
// Wire protocol (one JSON object per `data:` line, UTF-8):
//
//   event: stage   data: {"kind":"stage","stage":"retrieving"}
//   event: stage   data: {"kind":"stage","stage":"drafting"}
//   event: token   data: {"kind":"token","delta":"The first…"}
//   event: token   data: {"kind":"token","delta":" step is…"}
//   event: done    data: {"kind":"done","userMessage":{...},"coachMessage":{...},"sessionId":"..."}
//
// On failure the stream ends with:
//   event: error   data: {"kind":"error","code":"...","message":"..."}
//
// `token` events are emitted during the drafting phase and carry
// character-level deltas of the growing reply markdown. Clients append
// each delta to a live-rendered bubble; the final `done` event carries
// the authoritative, persisted CoachMessage (with citations, provider,
// server-side timestamps) that should replace the live bubble.
//
// Served from a Lambda Function URL (API Gateway HTTP APIs can't do SSE)
// routed by CloudFront behind the normal api.* subdomain. Auth: same
// Bearer-token verifyToken({pools:['consumer']}) pattern as /me/* routes.
export type CoachStreamStage = 'retrieving' | 'drafting';

export type CoachStreamEvent =
  | { kind: 'stage'; stage: CoachStreamStage }
  | { kind: 'token'; delta: string }
  | {
      kind: 'done';
      userMessage: CoachMessage;
      coachMessage: CoachMessage;
      sessionId: string;
    }
  | { kind: 'error'; code: string; message: string };

// ─── GET /me/coach/sessions/search?q=… ─────────────────────────────────────
//
// Keyword search across the user's sessions. Per designer Q15-Q20:
// - Rank: title match > summary match > message match, with a mild recency
//   boost so last week outranks last year at equal relevance.
// - Default scope: excludes archived (toggle on the SPA passes
//   `include_archived=true`).
// - V1 is keyword-only; semantic search is V1.x once the user hits ~100
//   sessions typical.

export interface CoachSessionSearchHit {
  /** The matching session's full metadata (same shape as list/get). */
  session: CoachSession;
  /** The best-match excerpt — a short snippet from title, summary or a
   *  message, with `<mark>…</mark>` tags around the matching terms. */
  snippet: string;
  /** Total number of matching message occurrences in this session. The
   *  snippet shows one; the UI surfaces "+{n-1} more matches in this session"
   *  when > 1. */
  matchCount: number;
  /** Where the match was found. */
  matchedIn: 'title' | 'summary' | 'message';
}

export interface SearchCoachSessionsResponse {
  hits: CoachSessionSearchHit[];
  /** True when the server stopped at the page limit; UI can show "load more". */
  hasMore: boolean;
  /** Total matches found (post-filter), useful for the "N RESULTS" header. */
  totalCount: number;
}

// ─── GET /me/coach/messages (DEPRECATED — flat-thread shim) ────────────────
//
// Pre-sessions-rail endpoint that returned the user's most recent messages
// across any session. Kept for backward-compat so the current Coach UI keeps
// working between MR A (this one) and MR B (Coach surface rebuild). The new
// surface uses GET /me/coach/sessions/{id}/messages instead.

export interface ListCoachMessagesResponse {
  messages: CoachMessage[];
  /** The session the current message tail belongs to. Renamed from
   *  `currentConversationId` for session vocabulary consistency. */
  currentSessionId: string;
}

// ─── POST /me/coach/feedback ───────────────────────────────────────────────

export interface SendCoachFeedbackRequest {
  /** Id of the COACH message being voted on. */
  messageId: string;
  verdict: 'up' | 'down';
  /** Optional free-text on thumbs-down. Server caps at ~500 chars. */
  reason?: string;
}

export interface SendCoachFeedbackResponse {
  feedback: {
    messageId: string;
    verdict: 'up' | 'down';
    createdAt: string;
  };
}
